import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, Tag, Modal, Button, message } from 'antd';
import { RobotOutlined, ReloadOutlined } from '@ant-design/icons';
import apiService from '../../services/api';
import { COLOR_PRIMARY } from '../../utils/colors';

const { Title } = Typography;

const AgentLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [retrying, setRetrying] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleRetry = async (id) => {
    setRetrying(id);
    try {
      const res = await apiService.agentLogs.retry(id);
      message.success(res.data.message || 'Retry complete');
      fetchLogs();
    } catch (err) {
      message.error(err.response?.data?.message || 'Retry failed');
    } finally {
      setRetrying(null);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await apiService.agentLogs.getAll();
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch agent logs', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Date & Time',
      dataIndex: 'receivedAt',
      key: 'receivedAt',
      render: (val) => new Date(val).toLocaleString('en-GB'),
      width: 180
    },
    {
      title: 'Total',
      dataIndex: 'totalJobs',
      key: 'totalJobs',
      width: 70,
      align: 'center'
    },
    {
      title: 'Success',
      dataIndex: 'successCount',
      key: 'successCount',
      width: 90,
      align: 'center',
      render: (val) => <Tag color="green">{val}</Tag>
    },
    {
      title: 'Failed',
      key: 'failedCount',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const count = record.totalJobs - record.successCount;
        return count > 0 ? <Tag color="red">{count}</Tag> : <Tag color="default">0</Tag>;
      }
    },
    {
      title: 'Error',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (val) => val ? <span style={{ color: 'red', fontSize: 12 }}>{val}</span> : '—'
    },
    {
      title: '',
      key: 'action',
      width: 160,
      render: (_, record) => {
        const hasFailed = record.totalJobs - record.successCount > 0;
        return (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setSelectedLog(record)} style={{ background: 'none', border: 'none', padding: 0, color: COLOR_PRIMARY, cursor: 'pointer' }}>View jobs</button>
            {hasFailed && record.rawText && (
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={retrying === record._id}
                onClick={() => handleRetry(record._id)}
              >
                Retry
              </Button>
            )}
          </div>
        );
      }
    }
  ];

  const jobColumns = [
    {
      title: 'Load ID',
      dataIndex: 'bookingReference',
      key: 'bookingReference'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (val) =>
        val === 'success'
          ? <Tag color="green">Success</Tag>
          : <Tag color="red">Failed</Tag>
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (val) => val || '—'
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <RobotOutlined style={{ fontSize: 20 }} />
          <Title level={4} style={{ margin: 0 }}>WhatsApp Agent Logs</Title>
        </div>
        <p style={{ color: '#666', marginBottom: 16 }}>
          Processing history from the WhatsApp job agent. Logs are retained for 30 days.
        </p>
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title={`Jobs — ${selectedLog && new Date(selectedLog.receivedAt).toLocaleString('en-GB')}`}
        open={!!selectedLog}
        onCancel={() => setSelectedLog(null)}
        footer={null}
        width={700}
      >
        <Table
          columns={jobColumns}
          dataSource={selectedLog?.jobs || []}
          rowKey={(r, i) => i}
          pagination={false}
          size="small"
        />
      </Modal>
    </div>
  );
};

export default AgentLogs;
